'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

// Cloudflare Turnstile site keys are intentionally public browser identifiers.
// The matching secret key must never be committed or exposed to client code.
const AULA_TURNSTILE_SITE_KEY = '0x4AAAAAAErxL15W9igIAP5s';
const TURNSTILE_SCRIPT_ID = 'aula-cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'compact' | 'flexible';
      retry?: 'auto' | 'never';
      callback: (token: string) => void;
      'error-callback'?: (code: string | number) => boolean | void;
      'expired-callback'?: () => void;
      'timeout-callback'?: () => void;
    }
  ) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

type TokenWaiter = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
  timeoutId: number;
};

export type TurnstileWidgetHandle = {
  getToken: () => string | null;
  reset: () => void;
  getFreshToken: () => Promise<string>;
};

type Props = {
  onTokenChange?: (token: string | null) => void;
  className?: string;
};

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, Props>(
  function TurnstileWidget({ onTokenChange, className = '' }, ref) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const tokenRef = useRef<string | null>(null);
    const callbackRef = useRef(onTokenChange);
    const waitersRef = useRef<TokenWaiter[]>([]);

    callbackRef.current = onTokenChange;

    function publishToken(token: string | null) {
      tokenRef.current = token;
      callbackRef.current?.(token);
    }

    function rejectWaiters(message: string) {
      const waiters = waitersRef.current.splice(0);
      for (const waiter of waiters) {
        window.clearTimeout(waiter.timeoutId);
        waiter.reject(new Error(message));
      }
    }

    function resolveWaiters(token: string) {
      const waiters = waitersRef.current.splice(0);
      for (const waiter of waiters) {
        window.clearTimeout(waiter.timeoutId);
        waiter.resolve(token);
      }
    }

    function resetWidget() {
      publishToken(null);
      const widgetId = widgetIdRef.current;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.reset(widgetId);
        } catch {
          // Turnstile will be rendered/retried by its own lifecycle if needed.
        }
      }
    }

    useImperativeHandle(ref, () => ({
      getToken() {
        return tokenRef.current;
      },
      reset() {
        resetWidget();
      },
      getFreshToken() {
        return new Promise<string>((resolve, reject) => {
          const timeoutId = window.setTimeout(() => {
            const index = waitersRef.current.findIndex(
              (waiter) => waiter.timeoutId === timeoutId
            );
            if (index >= 0) waitersRef.current.splice(index, 1);
            reject(new Error('security_check_timeout'));
          }, 20000);

          waitersRef.current.push({ resolve, reject, timeoutId });
          resetWidget();
        });
      },
    }));

    useEffect(() => {
      let disposed = false;

      const renderWidget = () => {
        if (
          disposed ||
          widgetIdRef.current ||
          !containerRef.current ||
          !window.turnstile
        ) {
          return;
        }

        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: AULA_TURNSTILE_SITE_KEY,
            theme: 'auto',
            size: 'flexible',
            retry: 'auto',
            callback(token) {
              publishToken(token);
              resolveWaiters(token);
            },
            'expired-callback'() {
              publishToken(null);
            },
            'timeout-callback'() {
              publishToken(null);
            },
            'error-callback'() {
              publishToken(null);
              rejectWaiters('security_check_failed');
              return true;
            },
          });
        } catch {
          publishToken(null);
          rejectWaiters('security_check_failed');
        }
      };

      const existing = document.getElementById(
        TURNSTILE_SCRIPT_ID
      ) as HTMLScriptElement | null;

      if (window.turnstile) {
        renderWidget();
      } else if (existing) {
        existing.addEventListener('load', renderWidget);
      } else {
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', renderWidget);
        document.head.appendChild(script);
      }

      return () => {
        disposed = true;
        existing?.removeEventListener('load', renderWidget);
        rejectWaiters('security_check_unmounted');

        const widgetId = widgetIdRef.current;
        widgetIdRef.current = null;
        if (widgetId && window.turnstile) {
          try {
            window.turnstile.remove(widgetId);
          } catch {}
        }
      };
    }, []);

    return (
      <div
        className={`min-h-[65px] w-full overflow-hidden ${className}`.trim()}
        aria-label="Security verification"
      >
        <div ref={containerRef} className="w-full" />
      </div>
    );
  }
);

export default TurnstileWidget;
